/**
 * Prepare a preflight inspection for pin — upload local rasters when needed (WP-D).
 */

import type { PreflightInspection } from './preflightTypes';
import { stageLocalRasterForPin } from './converterDropClient';

function geographicBbox(
  inspection: PreflightInspection,
): [number, number, number, number] | undefined {
  if (!inspection.bbox || inspection.bbox.length !== 4) return undefined;
  return [
    inspection.bbox[0],
    inspection.bbox[1],
    inspection.bbox[2],
    inspection.bbox[3],
  ];
}

/**
 * Ensure `inspection.url` is set. Local COG/GeoTIFF files are uploaded and
 * converted via the converter service before pin.
 */
export async function ensureInspectableUrl(
  inspection: PreflightInspection,
): Promise<PreflightInspection> {
  if (inspection.url?.trim()) return inspection;

  const file = inspection.droppedFile;
  if (!file) {
    throw new Error('Cannot pin: artifact URL is missing (convert first or drop a URL)');
  }

  if (inspection.detectedKind === 'cog') {
    const staged = await stageLocalRasterForPin(file, {
      crs: inspection.crs?.code,
      bbox: geographicBbox(inspection),
    });
    return {
      ...inspection,
      url: staged.cogUrl,
      fileName: staged.title,
      crs: { code: staged.sourceCrs, coordinateMode: 'georeferenced' },
      bbox: staged.bbox,
      droppedFile: undefined,
      droppedSidecars: undefined,
    };
  }

  throw new Error('Cannot pin: convert this file first or drop a hosted URL');
}
